import React, { useEffect, useRef, useState } from 'react';
import './WinnerCelebration.css';

interface WinnerCelebrationProps {
  isWinner: boolean;
  amount: number;
  currency: 'playcoins' | 'pkcash';
  handDescription?: string;
  loserHandDescription?: string;
  isFoldWin?: boolean;
  onComplete?: () => void;
}

const WinnerCelebration: React.FC<WinnerCelebrationProps> = ({
  isWinner,
  amount,
  currency,
  handDescription,
  loserHandDescription,
  isFoldWin,
  onComplete,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onCompleteRef = useRef(onComplete);
  const [showBanner, setShowBanner] = useState(false);
  const [chips, setChips] = useState<{ id: number; x: number; delay: number }[]>([]);

  // Keep ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!isWinner) {
      // Reset when not winner
      setShowBanner(false);
      setChips([]);
      return;
    }

    // Show banner
    setShowBanner(true);

    // Create falling chips
    const newChips = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 2,
    }));
    setChips(newChips);

    // Fire confetti
    fireConfetti();

    //  FIXED: Auto-hide after 3 seconds - use ref to avoid dependency issues

    const timer = setTimeout(() => {
      setShowBanner(false);
      setChips([]);
      onCompleteRef.current?.();
    }, 3000);

    return () => clearTimeout(timer);
  }, [isWinner]); //  Only depend on isWinner, not onComplete


  const fireConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Confetti particles
    const particles: Particle[] = [];
    const colors = ['#FFD700', '#00D9D9', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DFE6E9'];

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      size: number;
      rotation: number;
      rotationSpeed: number;
      shape: 'rect' | 'circle' | 'star';
      opacity: number;
    }

    // Create particles from multiple origins
    const createBurst = (originX: number, originY: number, count: number) => {
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
        const velocity = 8 + Math.random() * 8;
        particles.push({
          x: originX,
          y: originY,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity - 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 8 + Math.random() * 8,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
          shape: ['rect', 'circle', 'star'][Math.floor(Math.random() * 3)] as 'rect' | 'circle' | 'star',
          opacity: 1,
        });
      }
    };

    // Initial bursts from sides
    createBurst(canvas.width * 0.2, canvas.height * 0.5, 50);
    createBurst(canvas.width * 0.8, canvas.height * 0.5, 50);
    
    // Center burst
    setTimeout(() => createBurst(canvas.width * 0.5, canvas.height * 0.3, 80), 200);

    const drawStar = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
      const spikes = 5;
      const outerRadius = size;
      const innerRadius = size / 2;
      let rot = (Math.PI / 2) * 3;
      const step = Math.PI / spikes;

      ctx.beginPath();
      ctx.moveTo(x, y - outerRadius);

      for (let i = 0; i < spikes; i++) {
        ctx.lineTo(x + Math.cos(rot) * outerRadius, y + Math.sin(rot) * outerRadius);
        rot += step;
        ctx.lineTo(x + Math.cos(rot) * innerRadius, y + Math.sin(rot) * innerRadius);
        rot += step;
      }

      ctx.lineTo(x, y - outerRadius);
      ctx.closePath();
      ctx.fill();
    };

    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, index) => {
        // Update position
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.3; // gravity
        p.vx *= 0.99; // air resistance
        p.rotation += p.rotationSpeed;
        p.opacity -= 0.008;

        if (p.opacity <= 0) {
          particles.splice(index, 1);
          return;
        }

        // Draw particle
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          drawStar(ctx, 0, 0, p.size / 2);
        }

        ctx.restore();
      });

      if (particles.length > 0) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  };

  if (!isWinner) return null;

  const currencySymbol = currency === 'pkcash' ? '$' : '';

  const formattedAmount = currency === 'pkcash' 
    ? `$${amount.toFixed(2)}` 
    : `${amount.toLocaleString()}`;

  return (
    <div className="winner-celebration-overlay">
      {/* Confetti Canvas */}
      <canvas ref={canvasRef} className="confetti-canvas" />

      {/* Falling Chips */}
      <div className="falling-chips">
        {chips.map((chip) => (
          <div
            key={chip.id}
            className="falling-chip"
            style={{
              left: `${chip.x}%`,
              animationDelay: `${chip.delay}s`,
            }}
          >
            {currency === 'pkcash' ? '' : ''}

          </div>
        ))}
      </div>

      {/* Winner Banner */}
      {showBanner && (
        <div className="winner-banner">
          <div className="winner-glow" />
          <div className="winner-content">
            <div className="winner-icon"></div>

            <div className="winner-text">YOU WIN!</div>
            <div className="winner-amount">
              <span className="amount-value">{formattedAmount}</span>
              <span className="amount-label">
                {currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}
              </span>
            </div>
            {/* Hand explanation */}
            {isFoldWin ? (
              <div className="winner-hand-description">All opponents folded</div>
            ) : handDescription ? (
              <div className="winner-hand-description">
                {handDescription}
                {loserHandDescription && (
                  <span className="beating-text"> beating {loserHandDescription}</span>
                )}
              </div>
            ) : null}
            <div className="winner-sparkles">
              <span></span>

              <span>⭐</span>
              <span></span>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WinnerCelebration;